import React, { useState } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import SEOScoreCard from './components/SEOScoreCard';
import KeywordAnalyzer from './components/KeywordAnalyzer';
import MetaTagManager from './components/MetaTagManager';
import TechnicalAudit from './components/TechnicalAudit';
import CompetitorAnalysis from './components/CompetitorAnalysis';
import RankingTracker from './components/RankingTracker';
import SitemapGenerator from './components/SitemapGenerator';
import RobotsManager from './components/RobotsManager';
import SchemaMarkup from './components/SchemaMarkup';
import LLMTxtGenerator from './components/LLMTxtGenerator';

const SEOStudio = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard-home', icon: 'Home' },
    { label: 'SEO Studio', path: '/seo-studio' }
  ];

  const seoScores = [
    {
      score: 87,
      title: "Overall SEO Score",
      status: "excellent",
      trend: "up",
      details: [
        { label: "On-Page SEO", value: "92/100", status: "pass" },
        { label: "Technical SEO", value: "85/100", status: "pass" },
        { label: "Content Quality", value: "88/100", status: "pass" },
        { label: "User Experience", value: "83/100", status: "pass" }
      ]
    },
    {
      score: 92,
      title: "Content Optimization",
      status: "excellent",
      trend: "up",
      details: [
        { label: "Keyword Density", value: "Optimal", status: "pass" },
        { label: "Readability Score", value: "78/100", status: "pass" },
        { label: "Content Length", value: "1,847 words", status: "pass" },
        { label: "Internal Links", value: "12 links", status: "pass" }
      ]
    },
    {
      score: 78,
      title: "Mobile Performance",
      status: "good",
      trend: "stable",
      details: [
        { label: "Mobile Speed", value: "3.2s", status: "warning" },
        { label: "Mobile Usability", value: "95/100", status: "pass" },
        { label: "Responsive Design", value: "Passed", status: "pass" },
        { label: "Touch Elements", value: "Optimized", status: "pass" }
      ]
    },
    {
      score: 65,
      title: "Backlink Profile",
      status: "warning",
      trend: "down",
      details: [
        { label: "Total Backlinks", value: "1,247", status: "pass" },
        { label: "Referring Domains", value: "89", status: "warning" },
        { label: "Domain Authority", value: "42", status: "warning" },
        { label: "Toxic Links", value: "3 found", status: "fail" }
      ]
    }
  ];

  const sections = [
    { id: 'overview', label: 'Overview', icon: 'BarChart2' },
    { id: 'tools', label: 'SEO Tools', icon: 'Wrench' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={setSidebarCollapsed}
      />
      <Header
        isCollapsed={sidebarCollapsed}
        userName="Sarah Mitchell"
        userRole="Content Manager"
      />
      <main className={`main-content with-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="p-4 md:p-6 lg:p-8">
          <div className="mb-6">
            <Breadcrumbs items={breadcrumbItems} />
          </div>

          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              SEO Studio
            </h1>
            <p className="text-sm md:text-base text-gray-600">
              Comprehensive SEO management with automated recommendations and performance tracking
            </p>
          </div>

          {/* Section Tabs */}
          <div className="flex gap-2 mb-6 border-b border-gray-200">
            {sections?.map(section => (
              <button
                key={section?.id}
                onClick={() => setActiveSection(section?.id)}
                className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  activeSection === section?.id
                    ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <span>{section?.label}</span>
              </button>
            ))}
          </div>

          {activeSection === 'overview' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
                {seoScores?.map((score, index) => (
                  <SEOScoreCard
                    key={index}
                    score={score?.score}
                    title={score?.title}
                    status={score?.status}
                    trend={score?.trend}
                    details={score?.details}
                  />
                ))}
              </div>

              <div className="space-y-6 md:space-y-8">
                <KeywordAnalyzer />
                <MetaTagManager />
                <RankingTracker />
                <TechnicalAudit />
                <CompetitorAnalysis />
              </div>
            </>
          )}

          {activeSection === 'tools' && (
            <div className="space-y-6 md:space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-2">
                {[
                  { icon: 'Map', color: 'bg-green-100 text-green-700', label: 'Sitemap Generator', desc: 'Auto-generate sitemap.xml' },
                  { icon: 'Shield', color: 'bg-orange-100 text-orange-700', label: 'Robots.txt Manager', desc: 'Control crawler access' },
                  { icon: 'Code2', color: 'bg-indigo-100 text-indigo-700', label: 'Schema Markup', desc: 'Structured data per page' },
                  { icon: 'Bot', color: 'bg-violet-100 text-violet-700', label: 'LLM.txt Generator', desc: 'AI engine optimization' },
                ]?.map((tool, i) => (
                  <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${tool?.color?.split(' ')?.[0]}`}>
                      <svg className={`w-5 h-5 ${tool?.color?.split(' ')?.[1]}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        {tool?.icon === 'Map' && <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />}
                        {tool?.icon === 'Shield' && <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />}
                        {tool?.icon === 'Code2' && <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />}
                        {tool?.icon === 'Bot' && <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />}
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{tool?.label}</div>
                      <div className="text-xs text-gray-500">{tool?.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <SitemapGenerator />
              <RobotsManager />
              <SchemaMarkup />
              <LLMTxtGenerator />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SEOStudio;